import { useTrtcStore } from '@src/store/trtc';
import { FreeProductVersion } from '@src/types/types';
import { app } from '@tencent/tea-app';
import { RequestBody } from '@tencent/tea-app/lib/bridge';
import { notification } from '@tencent/tea-component';
import { t } from '../translate/t';
import { IS_NEW_USER_LASTEST_TIME } from '@src/component/create-app/hooks/useIsTRTCUser';

export const getApiRegion = () => {
  return { regionId: 9, region: 'ap-singapore' };
};
export interface Option {
  errorMessageMap?: any;
  finalErrorTip?: any;
  /**
   * 正常业务逻辑code 不报错提示
   */
  normalErrorCodes?: any;
  showErrorTip?: boolean;
}

function ServiceV3<T = any, REQ = any>(
  url: string,
  params: REQ,
  options: Option = {
    errorMessageMap: {},
    finalErrorTip: '',
    normalErrorCodes: [],
  },
) {
  const {
    errorMessageMap,
    finalErrorTip,
    normalErrorCodes,
    showErrorTip = true,
  } = options;
  return new Promise<T>((resolve, reject) => {
    const dt: RequestBody = {
      regionId: getApiRegion().regionId,
      serviceType: 'trtc',
      cmd: url,
      data: Object.assign(
        { Version: '2019-07-22', Region: getApiRegion().region },
        params,
      ),
    };
    app.capi
      .requestV3(dt, { tipErr: false, withLanguage: true })
      .then(async (response: any) => {
        resolve(response.data.Response);
      })
      .catch(async error => {
        if (showErrorTip) {
          const errorCode = error?.code;
          if (!normalErrorCodes?.includes?.(errorCode)) {
            if (errorMessageMap?.[errorCode]) {
              notification.error({ description: errorMessageMap?.[errorCode] });
            } else if (
              errorCode === 'AuthFailure.UnauthorizedOperation' ||
              errorCode === 'UnauthorizedOperation'
            ) {
              reject(error);
              seajs.use('nmc/sdk/sdk', async (sdk: any) => {
                const rtc = await sdk?.use?.('rtc-sdk');
                rtc?.cam?.showBreakModal({ message: error.message });
              });
            } else {
              const secondLevelCode = errorCode?.split?.('.')?.[1];
              const finalTips = secondLevelCode || finalErrorTip;
              finalTips && notification.error({ description: finalTips });
            }
          }
        }
        reject(error);
      });
  });
}

export async function GetFrontKeyVal(params: { Key: string }) {
  return ServiceV3('GetFrontKeyVal', params);
}

export async function SetFrontKeyVal(params: { Key: string; Val: string }) {
  return ServiceV3('SetFrontKeyVal', params);
}

export async function IsTrtcUser(params = {}) {
  return ServiceV3('IsTrtcUser', params, {
    finalErrorTip: t(
      'Failed to get user info. Please refresh and try again. If the problem persists, please submit a ticket or call our customer service.',
    ),
  }).then(data => {
    const isNewUser = Number(data.IsTrtcUser) === 0;
    const store = useTrtcStore.getState();
    if (isNewUser) {
      store?.setIsNewUserAtFirstSession(true);
      localStorage.setItem(
        IS_NEW_USER_LASTEST_TIME,
        String(new Date().valueOf()),
      );
    } else {
      store?.setIsTRTCUser(true);
    }
    return data;
  });
}

export async function CheckTRTCUserStatus(params = {}) {
  return ServiceV3('CheckTRTCUserStatus', params, {
    finalErrorTip: t(
      'Failed to get user info. Please refresh and try again. If the problem persists, please submit a ticket or call our customer service.',
    ),
  }).then(res => {
    return res;
  });
}
export async function DescribeTRTCLicense(params = {}) {
  return ServiceV3('DescribeTRTCLicense', params).then(res => res);
}
export async function DescribeMeasureResources(params = {}) {
  return ServiceV3('DescribeMeasureResources', params);
}

export async function DescribeUserBillingStrategy() {
  return ServiceV3<{
    SettlementType: 2 | 3;
    IsPostPay: boolean;
    IsProtected: boolean;
  }>(
    'DescribeUserBillingStrategy',
    {},
    { finalErrorTip: t('getting billing method failed, please try again') },
  );
}

export async function DescribeTRTCFreeLicenseLimit(
  params: {
    SdkAppId?: Number;
    BillParam?: FreeProductVersion;
  } = {},
) {
  if (params.SdkAppId) {
    params.SdkAppId = Number(params.SdkAppId);
  }
  return ServiceV3<{ SdkAppIdExceeded: boolean; AppIdExceeded: boolean }>(
    'DescribeTRTCFreeLicenseLimit',
    params,
  );
}

export async function DescribeHasUsefulLicense(params: any) {
  const { productType, ...restParams } = params;
  // 默认是 true 了
  const { hasUsefulLicenseProducts, setHasUsefulLicenseProducts } =
    useTrtcStore.getState();
  const hasUsefulLicense = hasUsefulLicenseProducts?.includes?.(productType);
  if (hasUsefulLicense) {
    return true;
  } else {
    return DescribeTRTCLicense(restParams).then(res => {
      if (res?.Total > 0) {
        const hasUsefulProducts = [...hasUsefulLicenseProducts, productType];
        setHasUsefulLicenseProducts(hasUsefulProducts);
      }
      return res?.Total > 0;
    });
  }
}

export async function ModifySignAgreement(params = {}) {
  return ServiceV3('ModifySignAgreement', params).then(res => {
    return res;
  });
}

export async function CheckTRTCUserStatusBanned(params = {}) {
  return ServiceV3('CheckTRTCUserStatus', params, {
    finalErrorTip: t(
      'Failed to get user info. Please refresh and try again. If the problem persists, please submit a ticket or call our customer service.',
    ),
  })
    .then(res => {
      const isBanned = !!res?.IsBannedUser || !!res?.IsBlackUser;
      return isBanned;
    })
    .catch(() => {
      return false;
    });
}
